import React, { useRef, useState, useCallback } from "react";
import {
  View,
  ScrollView,
  TouchableOpacity,
  Image,
  StyleSheet,
  Dimensions,
  Animated,
} from "react-native";
import { ODSText } from "../../atoms/ODSText";
import { ODSIcon } from "../../atoms/ODSIcon";
import ODSButton from "../../atoms/ODSButton";
import { getTokens } from "../../../design-tokens/color-tokens";

const { width: SCREEN_WIDTH } = Dimensions.get("window");

const CARD_GAP = 12;
const SIDE_MARGIN = 16;

// ── Shimmer Skeleton ─────────────────────────────────────────────────────────

const OfferSkeleton = ({ cardWidth }) => {
  const anim = useRef(new Animated.Value(0)).current;

  React.useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(anim, { toValue: 1, duration: 800, useNativeDriver: true }),
        Animated.timing(anim, { toValue: 0, duration: 800, useNativeDriver: true }),
      ])
    );
    loop.start();
    return () => loop.stop();
  }, [anim]);

  const bg = anim.interpolate({ inputRange: [0, 1], outputRange: ["#e0e0e0", "#f5f5f5"] });

  return (
    <View style={[styles.card, { width: cardWidth, marginRight: CARD_GAP }]}>
      <Animated.View style={[styles.skeletonImage, { backgroundColor: bg }]} />
      <View style={styles.metaRow}>
        <Animated.View style={[styles.skeletonLogo, { backgroundColor: bg }]} />
        <Animated.View style={[styles.skeletonBadge, { backgroundColor: bg }]} />
      </View>
      <Animated.View style={[styles.skeletonTitle, { backgroundColor: bg }]} />
      <Animated.View style={[styles.skeletonSubtitle, { backgroundColor: bg }]} />
    </View>
  );
};

// ── Offer Card ───────────────────────────────────────────────────────────────

const OfferCard = ({ offer, onPress, colors, cardWidth }) => {
  const subtitle = offer.verb && offer.saving && offer.merchant
    ? `${offer.verb} ${offer.saving} with ${offer.merchant}`
    : offer.merchant || "";

  return (
    <TouchableOpacity
      activeOpacity={0.85}
      onPress={() => onPress && onPress(offer)}
      style={[styles.card, { width: cardWidth, marginRight: CARD_GAP }]}
      accessible
      accessibilityRole="button"
      accessibilityLabel={`${offer.title}. ${offer.merchant}`}
    >
      <View style={styles.imageContainer}>
        {offer.background ? (
          <Image
            source={{ uri: offer.background }}
            style={styles.image}
            resizeMode="cover"
            accessibilityLabel={offer.title}
          />
        ) : (
          <View style={[styles.image, { backgroundColor: colors.neutral[200] }]} />
        )}
      </View>
      <View style={styles.metaRow}>
        {offer.image ? (
          <Image
            source={{ uri: offer.image }}
            style={styles.logo}
            resizeMode="contain"
            accessibilityLabel={`${offer.merchant} logo`}
          />
        ) : (
          <View style={styles.logoPlaceholder} />
        )}
        {offer.method ? (
          <View style={[styles.badge, { backgroundColor: colors.neutral[800] }]}>
            <ODSText type="xs" color={colors.neutral.white} marginTop={0}>
              {offer.method}
            </ODSText>
          </View>
        ) : null}
      </View>
      <View style={{ paddingHorizontal: 10, paddingBottom: 16, paddingTop: 10 }}>
        <ODSText
          type="sm-bold"
          color={colors.text.primary}
          marginTop={6}
          style={styles.paddedText}
          numberOfLines={2}
        >
          {offer.title}
        </ODSText>
        <ODSText
          type="xs"
          color={colors.text.secondary}
          marginTop={4}
          style={styles.subtitleText}
          numberOfLines={1}
        >
          {subtitle}
        </ODSText>
      </View>
    </TouchableOpacity>
  );
};

// ── Dot Indicators ───────────────────────────────────────────────────────────

const DotIndicators = ({ count, activeIndex, activeColor, inactiveColor }) => (
  <View style={styles.dotsRow}>
    {Array.from({ length: count }).map((_, i) => (
      <View
        key={i}
        style={[
          styles.dot,
          { backgroundColor: i === activeIndex ? activeColor : inactiveColor },
        ]}
      />
    ))}
  </View>
);

// ── ODSTopOffersCarousel ─────────────────────────────────────────────────────

const ODSTopOffersCarousel = ({
  offers = [],
  isLoading = false,
  isError = false,
  onOfferPress,
  onSeeMorePress,
}) => {
  const themeColors = getTokens("light").colors;
  const [activeIndex, setActiveIndex] = useState(0);
  const [containerWidth, setContainerWidth] = useState(SCREEN_WIDTH > 600 ? 600 : SCREEN_WIDTH);
  const scrollRef = useRef(null);

  // Determine cards visible based on container width
  const getCardsVisible = () => {
    if (containerWidth > 600) return 3; // Desktop
    if (containerWidth > 400) return 2; // Tablet
    return 1; // Mobile phone
  };

  const cardsVisible = getCardsVisible();

  // On mobile phone, make card slightly smaller so next card peeks from the right
  const peekWidth = cardsVisible === 1 ? 24 : 0;
  const totalGaps = CARD_GAP * (cardsVisible - 1);

  // Compute final card width
  const cardWidth = containerWidth > 0
    ? (containerWidth - (SIDE_MARGIN * 2) - totalGaps - peekWidth) / cardsVisible
    : 160;

  const snapInterval = cardWidth + CARD_GAP;
  const dotCount = offers.length > cardsVisible ? offers.length - cardsVisible + 1 : 0;
  const maxIndex = dotCount > 0 ? dotCount - 1 : offers.length - 1;

  const canScrollLeft = activeIndex > 0;
  const canScrollRight = activeIndex < maxIndex;

  const scrollLeft = useCallback(() => {
    if (scrollRef.current && activeIndex > 0) {
      const newIdx = activeIndex - 1;
      scrollRef.current.scrollTo({
        x: newIdx * snapInterval,
        animated: true,
      });
      setActiveIndex(newIdx);
    }
  }, [activeIndex, snapInterval]);

  const scrollRight = useCallback(() => {
    if (scrollRef.current && activeIndex < maxIndex) {
      const newIdx = activeIndex + 1;
      scrollRef.current.scrollTo({
        x: newIdx * snapInterval,
        animated: true,
      });
      setActiveIndex(newIdx);
    }
  }, [activeIndex, maxIndex, snapInterval]);

  const onLayout = useCallback((e) => {
    const w = e.nativeEvent.layout.width;
    if (w > 0) setContainerWidth(w);
  }, []);

  const handleScroll = useCallback((e) => {
    const offset = e.nativeEvent.contentOffset.x;
    if (snapInterval > 0) {
      const idx = Math.round(offset / snapInterval);
      const targetIndex = Math.min(Math.max(idx, 0), dotCount - 1);
      setActiveIndex(targetIndex);
    }
  }, [snapInterval, dotCount]);

  // ── Loading ──
  if (isLoading) {
    return (
      <View style={styles.wrapper} onLayout={onLayout}>
        {containerWidth > 0 && (
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={{ paddingLeft: SIDE_MARGIN }}
            scrollEnabled={false}
          >
            {Array.from({ length: cardsVisible }).map((_, i) => (
              <OfferSkeleton key={i} cardWidth={cardWidth} />
            ))}
          </ScrollView>
        )}
        <View style={styles.dotsRow}>
          {[0, 1, 2].map((i) => (
            <View key={i} style={[styles.dot, { backgroundColor: themeColors.neutral[300] }]} />
          ))}
        </View>
      </View>
    );
  }

  // ── Error ──
  if (isError) {
    return (
      <View style={styles.wrapper}>
        <ODSText
          type="xs"
          color={themeColors.feedback.error[500]}
          marginTop={8}
          style={{ textAlign: "center" }}
        >
          Unable to load offers. Please try again later.
        </ODSText>
      </View>
    );
  }

  // ── Empty ──
  if (!Array.isArray(offers) || offers.length === 0) return null;

  // ── Success ──
  return (
    <View style={styles.wrapper} onLayout={onLayout}>
      {containerWidth > 0 && (
        <View style={styles.carouselContainer}>
          {/* Left Indication Arrow */}
          {offers.length > 1 && (
            <TouchableOpacity
              activeOpacity={0.8}
              onPress={scrollLeft}
              disabled={!canScrollLeft}
              style={[
                styles.arrowButton,
                styles.leftArrowButton,
                !canScrollLeft && styles.arrowButtonDisabled,
              ]}
              accessibilityRole="button"
              accessibilityLabel="Scroll left"
            >
              <ODSIcon
                icon="TB-Arrow-Left"
                size={16}
                color={canScrollLeft ? '#ffffff' : themeColors.neutral[400]}
              />
            </TouchableOpacity>
          )}

          <ScrollView
            ref={scrollRef}
            horizontal
            pagingEnabled={false}
            showsHorizontalScrollIndicator={false}
            onScroll={handleScroll}
            scrollEventThrottle={16}
            decelerationRate="fast"
            snapToInterval={snapInterval}
            snapToAlignment="start"
            contentContainerStyle={{
              paddingLeft: SIDE_MARGIN,
              paddingRight: SIDE_MARGIN + peekWidth,
            }}
            style={{ width: containerWidth }}
            nestedScrollEnabled
          >
            {offers.map((offer, index) => (
              <OfferCard
                key={offer.id != null ? String(offer.id) : String(index)}
                offer={offer}
                onPress={onOfferPress}
                colors={themeColors}
                cardWidth={cardWidth}
              />
            ))}
          </ScrollView>

          {/* Right Indication Arrow */}
          {offers.length > 1 && (
            <TouchableOpacity
              activeOpacity={0.8}
              onPress={scrollRight}
              disabled={!canScrollRight}
              style={[
                styles.arrowButton,
                styles.rightArrowButton,
                !canScrollRight && styles.arrowButtonDisabled,
              ]}
              accessibilityRole="button"
              accessibilityLabel="Scroll right"
            >
              <ODSIcon
                icon="TB-Arrow-Right"
                size={16}
                color={canScrollRight ? '#ffffff' : themeColors.neutral[400]}
              />
            </TouchableOpacity>
          )}
        </View>
      )}

      {dotCount > 1 && (
        <View style={styles.navigationRow}>
          <TouchableOpacity
            activeOpacity={0.7}
            onPress={scrollLeft}
            disabled={!canScrollLeft}
            style={[styles.miniArrow, !canScrollLeft && { opacity: 0.3 }]}
          >
            <ODSIcon
              icon="TB-Arrow-Left"
              size={14}
              color={themeColors.main.pink[500]}
            />
          </TouchableOpacity>

          <DotIndicators
            count={dotCount}
            activeIndex={activeIndex}
            activeColor={themeColors.main.pink[500]}
            inactiveColor={themeColors.neutral[300]}
          />

          <TouchableOpacity
            activeOpacity={0.7}
            onPress={scrollRight}
            disabled={!canScrollRight}
            style={[styles.miniArrow, !canScrollRight && { opacity: 0.3 }]}
          >
            <ODSIcon
              icon="TB-Arrow-Right"
              size={14}
              color={themeColors.main.pink[500]}
            />
          </TouchableOpacity>
        </View>
      )}

      <View style={styles.buttonWrapper}>
        <ODSButton
          type="primary"
          themeColor={themeColors.main.pink}
          disabled={false}
          onPress={onSeeMorePress}
          restStyle={styles.seeMoreButton}
        >
          See More Rewards
        </ODSButton>
      </View>
    </View>
  );
};

// ── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  wrapper: {
    width: "100%",
    marginTop: 16,
    marginBottom: 8,
  },
  carouselContainer: {
    position: "relative",
    width: "100%",
    justifyContent: "center",
  },
  arrowButton: {
    position: "absolute",
    top: "40%",
    marginTop: -18,
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: "#EF5D9B",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 10,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 4,
    elevation: 4,
  },
  leftArrowButton: {
    left: 4,
  },
  rightArrowButton: {
    right: 4,
  },
  arrowButtonDisabled: {
    opacity: 0.4,
    backgroundColor: "#F8FAFC",
    borderColor: "#F1F5F9",
  },
  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    overflow: "hidden",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 3,
  },
  imageContainer: { width: "100%", height: 130, overflow: "hidden" },
  image: { width: "100%", height: "100%" },
  metaRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingRight: 10,
    paddingTop: 10,
  },
  logo: { width: 52, height: 26 },
  logoPlaceholder: { width: 52, height: 26 },
  badge: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 4 },
  paddedText: { paddingHorizontal: 12, fontSize: 13, lineHeight: 18 },
  subtitleText: { paddingHorizontal: 12, paddingBottom: 14, fontSize: 11 },
  navigationRow: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    marginTop: 12,
  },
  miniArrow: {
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  dotsRow: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
  },
  dot: { width: 8, height: 8, borderRadius: 4, marginHorizontal: 4 },
  buttonWrapper: { marginTop: 16, marginHorizontal: 16, alignItems: "center" },
  seeMoreButton: { width: "100%", maxWidth: 360, height: 48, borderRadius: 24 },
  // Skeleton
  skeletonImage: { width: "100%", height: 130 },
  skeletonLogo: { width: 48, height: 20, borderRadius: 4 },
  skeletonBadge: { width: 56, height: 20, borderRadius: 4 },
  skeletonTitle: { height: 14, borderRadius: 4, marginHorizontal: 12, marginTop: 8, width: "70%" },
  skeletonSubtitle: { height: 10, borderRadius: 4, marginHorizontal: 12, marginTop: 6, marginBottom: 14, width: "50%" },
});

export default ODSTopOffersCarousel;
